import React, { useMemo, useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import type {
  NewTaskAssignmentResult,
  TaskAssignmentResult
} from 'types/interfaces/TaskAssignmentResult';

import { TaskService } from 'components/services/TaskService';
import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

import AutoEmployeeAssignmentErrorsTable from './AutoEmployeeAssignmentErrorsTable';
import AutoEmployeeAssignmentMap from './AutoEmployeeAssignmentMap';
import AutoEmployeeAssignmentTable from './AutoEmployeeAssignmentTable';
import type { FailedTaskAssignmentRow } from './lib/assignmentErrors';

interface AutoEmployeeAssignmentDialogProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isTravelTimeOptimization?: boolean;
  tasksToAssignResponse?: TaskAssignmentResult[] | null;
  routes?: NewTaskAssignmentResult[];
  failedTasks?: FailedTaskAssignmentRow[];
  onApplied?: () => void | Promise<void>;
  onApply?: (taskIds: number[]) => Promise<unknown>;
  basicFooter?: boolean;
}

const AutoEmployeeAssignmentDialog: React.FC<
  AutoEmployeeAssignmentDialogProps
> = ({
  visible,
  setVisible,
  isTravelTimeOptimization = false,
  tasksToAssignResponse,
  routes = [],
  failedTasks = [],
  onApplied,
  onApply,
  basicFooter = false
}) => {
  const [loading, setLoading] = useState(false);

  const assignmentResults = useMemo(
    () => tasksToAssignResponse ?? [],
    [tasksToAssignResponse]
  );

  const showMap = isTravelTimeOptimization || basicFooter;

  const mapRoutes = useMemo(
    () =>
      routes.length > 0
        ? routes
        : (assignmentResults as unknown as NewTaskAssignmentResult[]),
    [routes, assignmentResults]
  );

  const distributedTaskIds = useMemo(() => {
    if (isTravelTimeOptimization) {
      return routes
        .flatMap((route) => route.tasks ?? [])
        .map((task) => Number(task.taskId))
        .filter((taskId) => !isNaN(taskId));
    }
    return assignmentResults
      .filter((task) => !task.errors || task.errors.length === 0)
      .map((task) => Number(task.taskId))
      .filter((taskId) => !isNaN(taskId));
  }, [isTravelTimeOptimization, routes, assignmentResults]);

  const hasSuccessfulAssignments = distributedTaskIds.length > 0;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      if (distributedTaskIds.length === 0) {
        setVisible(false);
        return;
      }

      if (onApply) {
        await onApply(distributedTaskIds);
      } else {
        await TaskService.AutoTaskEmployeeAssignment(distributedTaskIds, false);
      }
      await onApplied?.();
    } catch (error) {
      console.error('Error applying employee auto-assignment:', error);
    } finally {
      setLoading(false);
      setVisible(false);
    }
  };

  const renderFooter = () => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
      {basicFooter ? (
        <Button
          label="Закрыть"
          onClick={() => setVisible(false)}
          severity="secondary"
        />
      ) : (
        <>
          <Button
            label="Подтвердить"
            onClick={handleConfirm}
            disabled={!hasSuccessfulAssignments}
            severity={!hasSuccessfulAssignments ? 'secondary' : undefined}
          />
          <Button
            label="Внести корректировки"
            onClick={() => setVisible(false)}
            severity="secondary"
          />
        </>
      )}
    </div>
  );

  return (
    <Dialog
      header={
        isTravelTimeOptimization
          ? 'Результат распределения с учетом времени доезда'
          : 'Результат распределения'
      }
      visible={visible}
      style={{ width: '95vw', minWidth: 800, minHeight: 300 }}
      onHide={() => setVisible(false)}
      footer={renderFooter}
      pt={withDialogFooterSpacing()}
    >
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1000,
            borderRadius: 'inherit'
          }}
        >
          <ProgressSpinner />
        </div>
      )}

      {showMap ? (
        <>
          <AutoEmployeeAssignmentMap data={mapRoutes} />
          <AutoEmployeeAssignmentErrorsTable rows={failedTasks} />
        </>
      ) : (
        <AutoEmployeeAssignmentTable
          tasksToAssignResponse={assignmentResults}
        />
      )}
    </Dialog>
  );
};

export default AutoEmployeeAssignmentDialog;
